import { useEffect } from 'react';

/**
 * Fixes the React + Google Translate incompatibility.
 *
 * Problem: Google Translate wraps text nodes in <font> elements.
 * When React later tries to remove/update the original text node,
 * it's no longer a direct child — it's now inside a <font> wrapper —
 * causing "Failed to execute 'removeChild'" errors.
 *
 * Solution: Patch removeChild and insertBefore ONLY on the error path.
 * Instead of overriding the methods globally (which breaks React's
 * reconciliation), we wrap them so that when a "not a child" error
 * occurs, we search for the node inside Google Translate's <font>
 * wrappers and retry the operation on the correct parent.
 *
 * This is the approach recommended by the React team:
 * @see https://github.com/facebook/react/issues/11538#issuecomment-417504600
 */
const useGoogleTranslateFix = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Only apply if Google Translate is likely to be used
    // (we don't check for its presence — it loads dynamically)

    const originalRemoveChild = Node.prototype.removeChild;
    const originalInsertBefore = Node.prototype.insertBefore;

    // Overriding for Google Translate compatibility
    Node.prototype.removeChild = function <T extends Node>(child: T): T {
      if (child.parentNode !== this) {
        // The child may have been wrapped by Google Translate's <font> tag.
        // Try to find and remove from the actual parent instead.
        if (child.parentNode) {
          return originalRemoveChild.call(child.parentNode, child) as T;
        }
        // Node is already detached — no-op, return the child
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    // Overriding for Google Translate compatibility
    Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        // Reference node was wrapped by Google Translate's <font> tag.
        // Try to insert relative to the actual parent instead.
        if (referenceNode.parentNode) {
          return originalInsertBefore.call(referenceNode.parentNode, newNode, referenceNode) as T;
        }
        // Reference node is detached — just append to this node
        return originalInsertBefore.call(this, newNode, null) as T;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };
    return () => {
      Node.prototype.removeChild = originalRemoveChild;
      Node.prototype.insertBefore = originalInsertBefore;
    };
  }, []);
};
export { useGoogleTranslateFix };